/**
 * lib/reminderTemplate.ts
 * ------------------------------------------------------------------
 * THE WORDS THAT GO TO A PARENT ABOUT MONEY.
 *
 * PURE. A template and some facts in, a subject and a body out. No
 * Firestore, no clock, no mail transport — what a parent reads should be
 * decidable, and checkable, without sending anything.
 *
 * WHY THIS EXISTS. The reminder wording was hardcoded in a Cloud Function.
 * The bursar could edit the per-family message she copies into WhatsApp, but
 * the wording that actually went out — from the bulk send and from the 07:00
 * sweep, to every parent in arrears — she could not touch. The one message
 * the school sends most often was the one nobody at the school could change.
 *
 * TWO TEMPLATES, NOT ONE. A payment falling due next week and a payment
 * already overdue are different conversations, and a school that has to use
 * one form of words for both will pick the harsher one. `before` is a
 * courtesy; `after` is a chase.
 *
 * AN UNKNOWN PLACEHOLDER IS LEFT ALONE, DELIBERATELY. `{parnet}` renders as
 * `{parnet}` rather than as an empty gap, so the mistake is visible in the
 * preview — which renders through exactly this function — instead of
 * arriving at four hundred parents as "Dear ,". Saving one is refused
 * outright; this is the second line of defence, for a template saved before
 * a field was renamed.
 *
 * A MISSING VALUE IS NOT A MISSING PLACEHOLDER. `{child}` with no name
 * resolves to "your child", the way the hardcoded version always did. The
 * school does occasionally hold an invoice with no name attached, and the
 * parent should not be the one to find that out.
 * ------------------------------------------------------------------ */

export type ReminderKind = 'before' | 'after';

/**
 * Every placeholder a template may use, and what it means.
 *
 * Listed as data because the editor shows it to the bursar — a list of
 * fields kept only in a comment is a list she cannot read.
 */
export const REMINDER_FIELDS: { token: string; describes: string }[] = [
  { token: 'parent', describes: 'How the parent is addressed, e.g. "Dear Parent/Guardian"' },
  { token: 'child', describes: "The pupil's name" },
  { token: 'invoiceNo', describes: 'Invoice number, e.g. INV/2026/0413' },
  { token: 'instalment', describes: 'Which instalment, e.g. "Term 2 instalment"' },
  { token: 'amount', describes: 'The amount outstanding, formatted, e.g. P4,500.00' },
  { token: 'dueDate', describes: 'The date it fell (or falls) due' },
  { token: 'daysOverdue', describes: 'Whole days past the due date. 0 before it is due.' },
  { token: 'school', describes: "The school's name" },
  { token: 'motto', describes: "The school's motto" },
];

const KNOWN = new Set(REMINDER_FIELDS.map((f) => f.token));

export interface ReminderTemplate {
  subject: string;
  body: string;
}

export interface ReminderTemplates {
  before: ReminderTemplate;
  after: ReminderTemplate;
}

/**
 * What the school sends today.
 *
 * Kept verbatim from the hardcoded version in functions/finance.js, so
 * turning this feature on changes nothing until somebody edits it. A
 * migration that silently rewords four hundred emails is not a migration.
 */
/**
 * THE LINE AT THE FOOT OF EVERY REMINDER.
 *
 * On 23 Sep 2026 the sweep chased 26 families who owed nothing, five of them
 * substantially in credit, because the credit netting only guarded the
 * overdue branch. The netting is being fixed; this is the belt to that
 * braces. A reminder is written by a machine at a quarter to seven in the
 * morning and read by a parent who has no way of knowing that, and the
 * difference between a demand and an automated nudge is entirely in whether
 * the letter says which it is.
 *
 * NOT part of the editable template, deliberately. The bursar may reword the
 * body freely — that is the whole point of the template editor — and a safety
 * net that can be deleted while rewriting a sentence is not a safety net.
 * It is appended by the sender, shown in the preview so the screen does not
 * lie about what a parent receives, and cannot be switched off from the app.
 *
 * "crossed with this message" is doing real work: a parent who paid
 * yesterday needs to be told WHY the school appears not to know, or they
 * conclude their payment was lost.
 */
export const REMINDER_DISCLAIMER = 
  'This reminder is sent automatically. If you have already paid, or you have an agreed payment arrangement with the school, please ignore it \u2014 your payment may have crossed with this message. If you think it has reached you in error, please contact the bursar\u2019s office and we will check your account.';

export const DEFAULT_REMINDER_TEMPLATES: ReminderTemplates = {
  before: {
    subject: 'Payment reminder — {invoiceNo} ({child})',
    body: [
      'Dear {parent},',
      '',
      'This is a reminder that {instalment} of {amount} for {child} '
        + '(invoice {invoiceNo}) is due on {dueDate}.',
      '',
      'When paying, please quote {child} as the payment reference.',
      '',
      'Kind regards,',
      '{school}',
      '{motto}',
    ].join('\n'),
  },
  after: {
    subject: 'Overdue payment — {invoiceNo} ({child})',
    body: [
      'Dear {parent},',
      '',
      'This is a reminder that {instalment} of {amount} for {child} '
        + '(invoice {invoiceNo}) was due on {dueDate} and is now overdue.',
      '',
      'When paying, please quote {child} as the payment reference.',
      '',
      'Kind regards,',
      '{school}',
      '{motto}',
    ].join('\n'),
  },
};

export interface ReminderFacts {
  parent?: string;
  child?: string;
  invoiceNo?: string;
  instalment?: string;
  amount?: string;
  dueDate?: string;
  daysOverdue?: number;
  school?: string;
  motto?: string;
}

/** Sensible stand-ins, matching what the hardcoded version always said. */
const FALLBACK: Record<string, string> = {
  parent: 'Parent/Guardian',
  child: 'your child',
  invoiceNo: '',
  instalment: 'the instalment',
  amount: '',
  dueDate: '',
  daysOverdue: '0',
  school: '',
  motto: '',
};

/**
 * Fill a template.
 *
 * Known tokens are replaced; unknown ones are left exactly as written, so a
 * typo is visible in the preview rather than silently swallowed.
 */
export function renderReminder(template: string, facts: ReminderFacts): string {
  return String(template ?? '').replace(/\{(\w+)\}/g, (whole, token: string) => {
    if (!KNOWN.has(token)) return whole;
    const raw = (facts as Record<string, unknown>)[token];
    if (raw === undefined || raw === null || raw === '') return FALLBACK[token] ?? '';
    return String(raw);
  });
}

/** Both halves of one message. */
export function renderReminderMessage(
  t: ReminderTemplate,
  facts: ReminderFacts,
): { subject: string; body: string } {
  return {
    // A newline in a subject line is a header-injection vector and, more
    // prosaically, breaks the mail. Flattened rather than refused, because
    // a paste from Word should not stop a reminder going out.
    subject: renderReminder(t.subject, facts).replace(/[\r\n]+/g, ' ').trim(),
    body: renderReminder(t.body, facts),
  };
}

/**
 * Why this template cannot be saved, or null if it can.
 *
 * Held to a higher standard than an on-screen message, because nobody reads
 * this one before it goes: it is used unattended by the 07:00 sweep, for
 * every parent in arrears, until somebody changes it again.
 */
export function reminderTemplateProblem(t: ReminderTemplate): string | null {
  const subject = String(t?.subject ?? '').trim();
  const body = String(t?.body ?? '').trim();

  if (!subject) return 'Give the email a subject.';
  if (!body) return 'The message cannot be empty.';
  if (subject.length > 200) return 'That subject is too long for an email header.';
  if (body.length > 5000) return 'That message is too long — keep a reminder to a page.';

  /* ANY braces, not just word-shaped ones.
     This matched /\{(\w+)\}/, so `{30/04/26}` - slashes, not word characters -
     was not seen as a placeholder at all and sailed through the check. It was
     saved into the overdue template, and every parent chased with it was told
     their fees "was due on {30/04/26}", braces and all. If it is in braces,
     the author meant it as a field. */
  const unknown = [...new Set(
    [...`${subject}\n${body}`.matchAll(/\{([^{}]*)\}/g)]
      .map((m) => m[1])
      .filter((tok) => !KNOWN.has(tok)),
  )];
  if (unknown.length) {
    // Refused rather than stripped: {parnet} sent to four hundred parents is
    // a different kind of problem from "Dear ,", and both are avoidable here.
    return `${unknown.map((u) => `{${u}}`).join(', ')} ${unknown.length === 1 ? 'is not a field' : 'are not fields'} `
      + 'this message knows. Check the spelling against the list of fields.';
  }
  return null;
}

/**
 * Read the templates out of finance settings, falling back per field.
 *
 * PER FIELD, not per template. A school that has edited only the overdue
 * subject line keeps the default body, rather than losing it to a partial
 * record — which is the state a half-finished edit leaves behind.
 */
export function templatesFromSettings(
  settings: Record<string, unknown> | null | undefined,
): ReminderTemplates {
  const pick = (key: string, fallback: string): string => {
    const v = settings ? settings[key] : undefined;
    return typeof v === 'string' && v.trim() ? v : fallback;
  };
  return {
    before: {
      subject: pick('reminderSubjectBefore', DEFAULT_REMINDER_TEMPLATES.before.subject),
      body: pick('reminderBodyBefore', DEFAULT_REMINDER_TEMPLATES.before.body),
    },
    after: {
      subject: pick('reminderSubjectAfter', DEFAULT_REMINDER_TEMPLATES.after.subject),
      body: pick('reminderBodyAfter', DEFAULT_REMINDER_TEMPLATES.after.body),
    },
  };
}

/** The settings patch for a saved pair. */
export function settingsFromTemplates(t: ReminderTemplates): Record<string, string> {
  return {
    reminderSubjectBefore: t.before.subject,
    reminderBodyBefore: t.before.body,
    reminderSubjectAfter: t.after.subject,
    reminderBodyAfter: t.after.body,
  };
}

/** Has the school moved off the wording it started with? */
export function isDefaultTemplate(t: ReminderTemplate, kind: ReminderKind): boolean {
  const d = DEFAULT_REMINDER_TEMPLATES[kind];
  return t.subject === d.subject && t.body === d.body;
}
